import { type PropsWithChildren, useMemo } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createMarketplaceQueryCache } from 'components/marketplace/queryCache';
import { TourBookingSheet } from 'components/tours/TourBookingSheet';
import { useWebTourBookingGate } from 'components/tours/webTourBookingFlag';

import type { SelfTourAppHandoffPlacement } from './SelfTourAppHandoffModal';
import { SelfTourAppHandoffModal } from './SelfTourAppHandoffModal';

// The PDP render tree has no QueryClientProvider of its own (only /homes and
// /nl-search install one), so the self-tour surfaces bring theirs, wired to the
// marketplace QueryCache so query errors still reach Sentry. Everything below,
// including the flag query, must render inside it.
const SelfTourQueryClientProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: createMarketplaceQueryCache(),
        defaultOptions: { queries: { retry: false } },
      }),
    [],
  );
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

type SelfTourDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Called when a login-return replay wants the booking sheet opened. */
  onOpenRequest: () => void;
  placement: SelfTourAppHandoffPlacement;
  addressToken?: string;
  slug?: string;
  tourBookingListingId?: string | null;
  addressLine?: string;
};

const SelfTourDialogSurface = ({
  isOpen,
  onClose,
  onOpenRequest,
  placement,
  addressToken,
  slug,
  tourBookingListingId,
  addressLine,
}: SelfTourDialogProps) => {
  const gate = useWebTourBookingGate();
  if (gate.isResolved && gate.isEnabled && tourBookingListingId) {
    return (
      <TourBookingSheet
        isOpen={isOpen}
        onClose={onClose}
        onOpenRequest={onOpenRequest}
        listingId={tourBookingListingId}
        addressLine={addressLine}
      />
    );
  }
  return (
    <SelfTourAppHandoffModal
      isOpen={isOpen}
      onClose={onClose}
      placement={placement}
      addressToken={addressToken}
      slug={slug}
    />
  );
};

/**
 * The flag decision between the two self-tour surfaces: the booking sheet when
 * buyer-web-tour-booking is on and the listing is web-bookable, the two-QR
 * app-handoff modal otherwise (the fallback and the path for buyers who want
 * the app).
 */
export const SelfTourDialog = (props: SelfTourDialogProps) => (
  <SelfTourQueryClientProvider>
    <SelfTourDialogSurface {...props} />
  </SelfTourQueryClientProvider>
);
